import { useLazyQuery } from "@apollo/client";
import { Box, Divider, TextField, Typography } from "@mui/material";
import React, {
  ChangeEvent,
  KeyboardEvent,
  Ref,
  useEffect,
  useState,
} from "react";
import { useDebounce } from "use-debounce";
import { LOAD_AIRLINES_QUERY } from "../../globals/graphql";
import { Airline } from "../../types";
import { ConditionalAutocomplete } from "../react-hook-form/autocomplete/ConditionalAutocomplete";
import { RHFTransform } from "../react-hook-form/autocomplete/RHFAutocomplete";

type AirlineAutoCompleteProps = {
  id?: string;
  label?: string;
  onChange?: (event: any, value: any) => void;
  error?: boolean;
  helperText?: string;
  value: any;
  name?: string;
  placeholder?: string;
  inputRef?: Ref<any>;
  className?: any;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  inputClasses?: any;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  transform?: RHFTransform;
};

function AirlineAutoComplete(props: AirlineAutoCompleteProps) {
  const {
    id,
    label,
    onChange,
    error,
    value,
    inputRef,
    disabled,
    helperText,
    name,
    placeholder,
    inputClasses,
    required,
    fullWidth,
    onBlur,
    onKeyDown,
    transform,
  } = props;

  // state
  const [inputValue, setInputValue] = useState("");
  const [queryLoading, setQueryLoading] = useState(false);
  const [debouncedInput] = useDebounce(inputValue, 200);

  const [options, setOptions] = useState<Airline[]>([]);

  // queries
  const [getAirlines] = useLazyQuery(LOAD_AIRLINES_QUERY, {
    onCompleted(data) {
      const airlines = data.airlines.edges.map((edge) => edge.node);

      setQueryLoading(false);
      setOptions(airlines);
    },
    onError(error) {
      setQueryLoading(false);
      console.log(error);
    },
    fetchPolicy: "network-only",
  });

  // event handlers
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleUpdateField = (event: object, value: any) => {
    onChange(value, event);
  };

  useEffect(() => {
    if (debouncedInput === "") {
      setOptions([]);
      return undefined;
    }

    if (debouncedInput.length) {
      getAirlines({
        variables: {
          globalSearch: debouncedInput,
        },
      });
    }
  }, [debouncedInput, getAirlines]);

  // display loading as soon as user makes an input change
  useEffect(() => {
    setQueryLoading(!!inputValue);
  }, [inputValue]);

  return (
    <ConditionalAutocomplete
      name={name}
      fullWidth
      autoHighlight
      disabled={disabled}
      onChange={handleUpdateField}
      id={id}
      getOptionLabel={(option: Airline) =>
        typeof option === "string"
          ? option
          : `${option.iataCode} - ${option.airlineName}`
      }
      isOptionEqualToValue={(option: Airline, value: Airline) =>
        option.icaoCode === value.icaoCode
      }
      filterOptions={(x) => x}
      options={options}
      loading={queryLoading}
      loadingText="Loading..."
      noOptionsText={inputValue ? "No Results Found" : "Enter Airline Name"}
      autoComplete={false}
      includeInputInList
      placeholder={placeholder}
      value={value}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      transform={transform}
      renderInput={(params) => {
        const { InputProps, ...restParams } = params;
        return (
          <TextField
            inputRef={inputRef}
            error={error}
            helperText={helperText}
            {...restParams}
            InputProps={{
              ...InputProps,
              classes: inputClasses,
              autoComplete: "new-password",
            }}
            label={label}
            variant="outlined"
            name={name}
            fullWidth={fullWidth}
            required={required}
            onChange={handleChange}
          />
        );
      }}
      renderOption={(props, { airlineName, iataCode }) => (
        <li {...props}>
          <Box>
            <Typography variant="body2">{airlineName}</Typography>
            <Typography style={{ fontSize: "12px" }}>{iataCode}</Typography>
          </Box>
          <Divider />
        </li>
      )}
    />
  );
}

export default AirlineAutoComplete;
